import React from 'react';
import { Page } from '../../layout';
import { Breadcrumb } from '@shared/components/breadcrumb/Breadcrumb';
import PageInstructions from '@shared/components/PageInstructions';
import AppraiserCalendar from './AppraiserCalendar/AppraiserCalendar';
import AdminCalendar from './AdminCalendar/AdminCalendar';
import ATMCalendar from './ATMCalendar/ATMCalendar';
import { PAGE_MODE } from './shared/constants';
import useCalendarPageMode from './shared/hooks/useCalendarPageMode';
import useDefaultUserActingAsATM from './shared/hooks/useDefaultUserActingAsATM';

const CalendarPage = () => {
  const isUserActingAsATM = useDefaultUserActingAsATM();

  const pageMode = useCalendarPageMode(isUserActingAsATM);
  const settings = pageSettings.get(pageMode);

  const pageTitle = 'Calendar';
  const breadcrumbPathItems = [{ name: pageTitle, url: '' }];
  const title = 'Calendar';
  const description = settings.description;
  const Calendar = settings.component;

  return (
    <Page title={pageTitle} className='calendar-page'>
      <Breadcrumb pathItems={breadcrumbPathItems} />
      <PageInstructions title={title} description={description} />
      <Calendar />
    </Page>
  );
};

const pageSettings = new Map([
  [
    PAGE_MODE.VIP_ADMIN,
    {
      description:
        'Navigate to the desired week by clicking the previous and/or next week arrows ' +
        'or by opening the calendar pop-up and selecting a date. Available appraisers ' +
        'will be displayed to the left of the calendar. Events will be color coded to ' +
        'indicate status according to the legend. Select an event to view and edit the ' +
        'details. Create a new event by clicking the “Add Event” button.',
      component: AdminCalendar
    }
  ],
  [
    PAGE_MODE.ISACA_APPRAISER_ATL,
    {
      description:
        'Find available appraisal opportunities on this calendar. ' +
        'Update your availability using the weekly checkboxes. ' +
        'Checkboxes default to checked, which demonstrates availability. ' +
        'Uncheck boxes for weeks that are not available for appraisal work.',
      component: AppraiserCalendar
    }
  ],
  [
    PAGE_MODE.ATM,
    {
      description:
        'Navigate to the desired week by clicking the previous and/or next week arrows ' +
        'or by opening the calendar pop-up and selecting a date. Events will be color coded to ' +
        'indicate status according to the legend. ' +
        'Filter events by clicking on the status in the legend. ' +
        'Select an event to view the details. ' +
        'Find available appraisal opportunities on this calendar. ' +
        'Update your availability using the weekly checkboxes. ' +
        'Checkboxes default to checked, which demonstrates availability. ' +
        'Uncheck boxes for weeks that are not available for appraisal work.',
      component: ATMCalendar
    }
  ],
  [
    PAGE_MODE.ORG_UNIT,
    {
      description:
        'The calendar below displays all active appraisal events for the unit. ' +
        'Navigate to the desired week by clicking the previous and/or next week arrows ' +
        'or by opening the calendar pop-up and selecting a date. ' +
        'Filter events by clicking on the status in the legend. ' +
        'Unit availability can be updated using the checkbox. ' +
        'Checkboxes default to checked, which demonstrates ' +
        'availability. Uncheck boxes for weeks that the unit is not available for an appraisal. ' +
        'Use the dropdown to the right of the date picker to view all affiliated Internal ATM(s), ' +
        'then select one to view their individual availability. Internal ATM availability for ' +
        'the week can also be updated using the checkbox. Events will be color coded to indicate ' +
        'status according to the legend. Select an event to view and edit the details.',
      component: ATMCalendar
    }
  ],
  [
    PAGE_MODE.ORG,
    {
      description:
        'The calendar below displays all active appraisal events affiliated unit(s). ' +
        'Navigate to the desired week by clicking the previous and/or next week arrows or ' +
        'by opening the calendar pop-up and selecting a date. ' +
        'Filter events by clicking on the status in the legend. ' +
        'Use the dropdown to the right of the date picker to view all affiliated Internal ATM(s), then select one to view ' +
        'their individual availability. Internal ATM availability for the week can be updated ' +
        'using the checkbox. Checkboxes default to checked, which demonstrates availability. ' +
        'Uncheck boxes for weeks that individuals are not available for an appraisal. ' +
        'Events will be color coded to indicate status according to the legend. ' +
        'Select an event to view and edit the details.',
      component: ATMCalendar
    }
  ],
  [
    PAGE_MODE.ORG_UNIT_ATM,
    {
      description:
        'The calendar below displays all active appraisal events for the unit. ' +
        'Navigate to the desired week by clicking the previous and/or next week arrows ' +
        'or by opening the calendar pop-up and selecting a date. ' +
        'Filter events by clicking on the status in the legend. ' +
        'Unit availability can be updated using the checkbox. Checkboxes default to checked, which demonstrates ' +
        'availability. Uncheck boxes for weeks that the unit is not available for an appraisal. ' +
        'Use the dropdown to the right of the date picker to view all affiliated Internal ATM(s), ' +
        'then select one to view their individual availability. Internal ATM availability for ' +
        'the week can also be updated using the checkbox. Events will be color coded to indicate ' +
        'status according to the legend. Select an event to view and edit the details. ' +
        'Select yourself from the Internal ATM dropdown list to bid on an Event and express ' +
        'your interest in participating.',
      component: ATMCalendar
    }
  ],
  [
    PAGE_MODE.ORG_ATM,
    {
      description:
        'The calendar below displays all active appraisal events affiliated unit(s). ' +
        'Navigate to the desired week by clicking the previous and/or next week arrows or ' +
        'by opening the calendar pop-up and selecting a date. ' +
        'Filter events by clicking on the status in the legend. ' +
        'Use the dropdown to the right of the date picker to view all affiliated Internal ATM(s), then select one to view ' +
        'their individual availability. Internal ATM availability for the week can be updated ' +
        'using the checkbox. Checkboxes default to checked, which demonstrates availability. ' +
        'Uncheck boxes for weeks that individuals are not available for an appraisal. ' +
        'Events will be color coded to indicate status according to the legend. ' +
        'Select an event to view and edit the details. ' +
        'Select yourself from the Internal ATM dropdown list to bid on an Event and express ' +
        'your interest in participating.',
      component: ATMCalendar
    }
  ]
]);

export default CalendarPage;
